import { getPwaBasePath } from "./pwaBrand";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PUSH_OPT_IN_KEY = "intrapaf_push_opt_in";

export function getPushOptInPreference() {
  try {
    return localStorage.getItem(PUSH_OPT_IN_KEY) === "true";
  } catch (e) {
    return false;
  }
}

export function setPushOptInPreference(enabled) {
  try {
    localStorage.setItem(PUSH_OPT_IN_KEY, enabled ? "true" : "false");
  } catch (e) {
    // ignore
  }
}

export function isPushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function getActivePushSubscription() {
  if (!isPushSupported()) {
    return null;
  }

  const registration = await navigator.serviceWorker.getRegistration("/");
  if (!registration) {
    return null;
  }

  return registration.pushManager.getSubscription();
}

export async function getPushClientState() {
  if (!isPushSupported()) {
    return {
      permission: "unsupported",
      hasSubscription: false,
      subscription: null,
    };
  }

  const permission = Notification.permission;
  const subscription = await getActivePushSubscription();

  return {
    permission,
    hasSubscription: Boolean(subscription),
    subscription,
  };
}

export async function syncPushSubscriptionWithServer(
  apiGet,
  apiPost,
  vapidUrl,
  subscribeUrl
) {
  if (!isPushSupported() || Notification.permission !== "granted") {
    return false;
  }

  const publicKey = await fetchVapidPublicKey(apiGet, vapidUrl);
  if (!publicKey) {
    return false;
  }

  const registration = await navigator.serviceWorker.register("/sw.js", {
    scope: "/",
  });
  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const json = subscription.toJSON();
  await apiPost(subscribeUrl, {
    endpoint: json.endpoint,
    keys: json.keys,
  });

  return true;
}

export async function fetchVapidPublicKey(apiGet, apiUrl) {
  try {
    const response = await apiGet(apiUrl);
    return (response.data && response.data.publicKey) || null;
  } catch (e) {
    return null;
  }
}

export async function subscribeToPush(apiGet, apiPost, vapidUrl, subscribeUrl) {
  if (!isPushSupported()) {
    return "unsupported";
  }

  const publicKey = await fetchVapidPublicKey(apiGet, vapidUrl);
  if (!publicKey) {
    return "server-unavailable";
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return "denied";
  }

  const registration = await navigator.serviceWorker.register("/sw.js", {
    scope: "/",
  });
  await navigator.serviceWorker.ready;

  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const json = subscription.toJSON();
  await apiPost(subscribeUrl, {
    endpoint: json.endpoint,
    keys: json.keys,
  });

  return "granted";
}

export async function unsubscribeFromPush(apiDelete, unsubscribeUrl) {
  const registration = await navigator.serviceWorker.getRegistration("/");
  const subscription = registration
    ? await registration.pushManager.getSubscription()
    : null;
  const endpoint = subscription ? subscription.endpoint : null;

  if (subscription) {
    await subscription.unsubscribe();
  }

  await apiDelete(unsubscribeUrl, {
    data: endpoint ? { endpoint } : {},
  });
}

export async function getNotificationPermission() {
  if (!isPushSupported()) {
    return "unsupported";
  }
  return Notification.permission;
}

export function getDefaultPushIcon() {
  return `${getPwaBasePath()}/icons/icon-192.png`;
}
